import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { gql, useMutation } from '@apollo/client';
import IntlMessages from 'helpers/IntlMessages';
import { useIntl } from 'react-intl';
import NotificationManager from 'components/common/react-notifications/NotificationManager';
import { useHistory, useParams } from 'react-router-dom';
import { Scalars } from 'services/graphqltypes';
import CustomRange from 'components/FormComponents/CustomRange';

const APPROVE_PUBLISH_SPECIMEN = gql`
mutation approvePublishSpecimen(
  $authorId: UUID!
  $reviewerId: UUID!
  $specimenId: UUID!
  $learningPathId: UUID!
  $authorAmount: Int!
  $reviewerAmount: Int!
  $commonsSlug: String!
) {
  approvePublishSpecimen: updateSpeciman(
    input: { patch: { status: APPROVED_PUBLISHED }, id: $specimenId }
  ) {
    speciman {
      id
      status
    }
    query {
      territoryBySlug(slug: $commonsSlug) {
        specimen(filter: { status: { equalTo: APPROVED_PUBLISHED } }) {
          totalCount
        }
      }
    }
  }
  createAuthorImpactPoints: createUserCredit(
    input: {
      userCredit: {
        userId: $authorId
        awardedById: $reviewerId
        specimenId: $specimenId
        learningPathId: $learningPathId
        amount: $authorAmount
        isDevelopment: true
      }
    }
  ) {
    userCredit {
      id
      isDevelopment
      awardedById
      amount
      learningPathId
      specimenId
      userId
    }
  }
  createReviewerImpactPoints: createUserCredit(
    input: {
      userCredit: {
        userId: $reviewerId
        awardedById: $reviewerId
        specimenId: $specimenId
        learningPathId: $learningPathId
        amount: $reviewerAmount
        isDevelopment: true
      }
    }
  ) {
    userCredit {
      id
      isDevelopment
      awardedById
      amount
      learningPathId
      specimenId
      userId
    }
  }
}
`;

type ApproveModalProps = {
  showApproveModal: boolean;
  setShowApproveModal: React.Dispatch<React.SetStateAction<boolean>>;
  specimenId: Scalars['UUID'];
  authorId: Scalars['UUID'];
  reviewerId: Scalars['UUID'];
}

const ApproveModal = ({
  showApproveModal,
  setShowApproveModal,
  specimenId,
  authorId,
  reviewerId,
}: ApproveModalProps): React.ReactElement => {
  const intl = useIntl();
  const history = useHistory();
  const { commonsSlug, slug } = useParams() as any;

  const [authorIp, setAuthorIp] = useState<number>(25);
  const [reviewerIP, setReviewerIP] = useState<number>(25);
  const [clickedApprove, setClickedApprove] = useState(false);
  const [approvePublishSpecimen] = useMutation(APPROVE_PUBLISH_SPECIMEN);
  const [submitError, setSubmitError] = useState<string>('');

  const approvePublishHandler = () => {
    setClickedApprove(true);
    approvePublishSpecimen({
      variables: {
        specimenId: specimenId,
        authorId: authorId,
        reviewerId: reviewerId,
        learningPathId: '1396b695-f152-43cf-8927-4e84e56979b4', // Botany
        authorAmount: authorIp,
        reviewerAmount: reviewerIP,
        commonsSlug: commonsSlug,
      }
    }).then(() => {
      NotificationManager.success(
        intl.formatMessage({ id: 'bioregion.specimen.dashboard.approve' }),
        null,
        3000,
        null,
        null,
        'filled'
      );
      setShowApproveModal(false);
      history.push(`/commons/${commonsSlug}/specimen/${slug}#overview`);
    }).catch((error) => {
      setSubmitError(error.message);
      NotificationManager.warning(
        `Error: ${error.message}`,
        null,
        3000,
        null,
        null,
        'filled'
      );
      setClickedApprove(false); // let the user try again
    });

  };

  return (
    <>
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} className="rounded-modal">
        <Modal.Header closeButton className="pb-2">
          <Modal.Title>
            <h1 className="pb-0 mb-0"><IntlMessages id="bioregion.specimen.dashboard.approve" /></h1>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div>
            <p><b><IntlMessages id="bioregion.specimen.approve-description-1" /></b></p>
            <p><IntlMessages id="bioregion.specimen.approve-description-2" /></p>
          </div>

          <div className='form-group'>

            <CustomRange
              min={0}
              max={50}
              step={5}
              showTickLabel={false}
              handleOnChange={(value) => {
                setAuthorIp(value);
                setReviewerIP(50 - value);
              }}
            />
            <datalist id="tickmarks">
              <option value="0"></option>
              <option value="5"></option>
              <option value="10"></option>
              <option value="15"></option>
              <option value="20"></option>
              <option value="25"></option>
              <option value="30"></option>
              <option value="35"></option>
              <option value="40"></option>
              <option value="45"></option>
              <option value="50"></option>
            </datalist>
            <div className='d-flex flex-row  justify-content-around'>
              <label htmlFor="IpRange" id="authorIp" className="text-primary"><IntlMessages id="bioregion.specimen.author" />: {authorIp}</label>
              <label htmlFor="IpRange" id="reviewerIp" className="accent-color"><IntlMessages id="bioregion.specimen.reviewer" />: {reviewerIP}</label>
            </div>
          </div>


          { // error message
            submitError &&
            <div className="form-field-error mb-2 text-center" style={{ 'color': 'red' }}>Error: {submitError}</div>
          }
          <div className="d-flex mx-auto justify-content-around">
            <Button
              className="mr-4 btn btn-outline-primary btn-lg"
              disabled={false}
              variant="outline-primary"
              onClick={() => setShowApproveModal(false)}
            >
              <IntlMessages id="general.back" />
            </Button>

            <Button
              className="mr-4 btn btn-yellow btn-lg"
              type="submit"
              disabled={clickedApprove}
              onClick={() => {
                approvePublishHandler();
              }
              }
            >
              <IntlMessages id="bioregion.specimen.dashboard.approve" />
            </Button>
          </div>
        </Modal.Body>
        <div className="dropdown-divider mx-4"></div>
        <Modal.Body>
          <p><b><IntlMessages id="bioregion.specimen.quick-guide" />:</b></p>

          {/* perfect range example */}
          <div className='form-group'>
            <label htmlFor="perfectRange"><IntlMessages id="bioregion.specimen.quick-guide-perfect" />: </label>
            <input type="range" id="perfectRange"
              className="form-control-range"
              min="0"
              max="50"
              step="5"
              list="tickmarks"
              value="45"
            // disabled={true}
            />
            <datalist id="tickmarks">
              <option value="0"></option>
              <option value="5"></option>
              <option value="10"></option>
              <option value="15"></option>
              <option value="20"></option>
              <option value="25"></option>
              <option value="30"></option>
              <option value="35"></option>
              <option value="40"></option>
              <option value="45"></option>
              <option value="50"></option>
            </datalist>
            <div className='d-flex flex-row  justify-content-around'>
              <label htmlFor="perfectRange" className="text-primary"><IntlMessages id="bioregion.specimen.author" />: 45</label>
              <label htmlFor="perfectRange" className="accent-color"><IntlMessages id="bioregion.specimen.reviewer" />: 5</label>
            </div>
          </div>

          {/* typos and grammar range example */}
          <div className='form-group'>
            <label htmlFor="typosRange"><IntlMessages id="bioregion.specimen.quick-guide-typos" />: </label>
            <input type="range" id="typosRange"
              className="form-control-range"
              min="0"
              max="50"
              step="5"
              list="tickmarks"
              value="40"
            // disabled={true}
            />
            <datalist id="tickmarks">
              <option value="0"></option>
              <option value="5"></option>
              <option value="10"></option>
              <option value="15"></option>
              <option value="20"></option>
              <option value="25"></option>
              <option value="30"></option>
              <option value="35"></option>
              <option value="40"></option>
              <option value="45"></option>
              <option value="50"></option>
            </datalist>
            <div className='d-flex flex-row  justify-content-around'>
              <label htmlFor="typosRange" className="text-primary"><IntlMessages id="bioregion.specimen.author" />: 40</label>
              <label htmlFor="typosRange" className="accent-color"><IntlMessages id="bioregion.specimen.reviewer" />: 10</label>
            </div>
          </div>

          {/* further research range example */}
          <div className='form-group'>
            <label htmlFor="researchRange"><IntlMessages id="bioregion.specimen.quick-guide-research" />: </label>
            <input type="range" id="researchRange"
              className="form-control-range"
              min="0"
              max="50"
              step="5"
              list="tickmarks"
              value="30"
            // disabled={true}
            />
            <datalist id="tickmarks">
              <option value="0"></option>
              <option value="5"></option>
              <option value="10"></option>
              <option value="15"></option>
              <option value="20"></option>
              <option value="25"></option>
              <option value="30"></option>
              <option value="35"></option>
              <option value="40"></option>
              <option value="45"></option>
              <option value="50"></option>
            </datalist>
            <div className='d-flex flex-row  justify-content-around'>
              <label htmlFor="researchRange" className="text-primary"><IntlMessages id="bioregion.specimen.author" />: 30</label>
              <label htmlFor="researchRange" className="accent-color"><IntlMessages id="bioregion.specimen.reviewer" />: 20</label>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ApproveModal;
